/**
 * <ScreenBackground> — le fond d'un écran, peint par la COQUILLE (template).
 *
 * Le fond d'un écran n'appartient pas à la page qu'il porte. Le header de l'espace parent ne pose
 * aucune surface : il flotte, et ce qu'on voit derrière lui doit être le même fond que derrière
 * la page, sans arête entre les deux. Et la lueur d'accueil part du coin haut-droit de l'ÉCRAN —
 * là où vit le header —, pas du haut de la zone qui défile. Un seul endroit peut donc la peindre :
 * celui qui contient header et page à la fois.
 *
 * Ce gabarit peint la couleur de page (`surface`) et, quand l'écran la porte, la lueur. Tout
 * `<PageTemplate>` monté dedans se sait peint et ne pose plus son propre fond : un fond opaque de
 * page couvrirait la lueur sous le header. Hors de lui — un écran poussé sur une pile —, la page
 * garde le sien.
 *
 * Qui l'appelle : la coquille des onglets parent (`ports/.../ParentTabBackground`, où
 * l'enregistrement de l'onglet dit lequel porte la lueur) et le harnais Storybook `<ParentSpace>`.
 * Le design system ne connaît toujours aucune route.
 */
import React from "react"
import { View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Glow } from "../../molecules/Glow"
import { ScreenPaintedContext } from "./screenPainted"

export type ScreenBackgroundProps = {
    children: React.ReactNode
    /**
     * L'écran porte la lueur d'accueil. Rien par défaut, et ce défaut compte : le livre ne
     * l'accorde qu'à l'accueil de chaque espace (plus la connexion et la réussite), « jamais sur
     * une liste, un formulaire ou des réglages ».
     */
    glow?: boolean
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function ScreenBackground(props: ScreenBackgroundProps) {
    const { children, glow = false, style, testID } = props

    return (
        <View testID={testID} style={[styles.fill, style]}>
            {glow && <Glow />}
            <ScreenPaintedContext.Provider value={true}>{children}</ScreenPaintedContext.Provider>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    fill: {
        flex: 1,
        backgroundColor: theme.colors.surface,
    },
}))
